<template>
  <page content-class="admin-campaign-list">
    <campaign-editor :campaign="campaign" step="stages">

      <div class="d-flex justify-content-center align-items-middle my-5" v-if="!campaign">
        <b-spinner variant="primary"/>
      </div>

      <b-row v-else class="stages-editor m-0">

        <!-- LEFT SIDE -->
        <b-col md="3" class="border-right p-0">
          <div class="d-flex flex-column justify-content-between h-100">
            <div class="d-flex justify-content-center p-3 border-bottom">
              <span>Start</span>
            </div>
            <div class="d-flex flex-column justify-content-start align-items-center flex-fill py-2 px-2">
              <div class="border vertical-line"></div>
              <b-card
                v-for="component in form.components"
                :key="component.id"
                class="w-100 mb-2 cursor-pointer"
                @click="changeComponent(component)"
                :style="{
                  background: activeComponent && component.id === activeComponent.id ? (form.branding ? form.branding.colour : '#5f27cd') : '#fff',
                  color: activeComponent && component.id === activeComponent.id ? '#fff !important' : 'inherit',
                }"
              >
                <div class="d-flex justify-content-start align-items-center">
                  <!-- <div>{{ componentInfo(component) }}</div> -->
                  <div class="pr-2" :style="{width: '36px', fontSize: '24px' }">
                    <font-awesome-icon :icon="componentInfo(component).icon" />
                  </div>
                  <div>
                    <span>{{ componentInfo(component).label }}</span>
                    <!-- <div>{{ componentInfo(component) }}</div> -->
                    <template v-if="['email', 'slack', 'reminder'].includes(componentInfo(component).key)">
                      <br><span style="font-size: 13px" :class="{ 'text-muted': !(activeComponent && component.id === activeComponent.id) }">
                        <template v-if="componentInfo(component).startDate">
                          Send date: {{componentInfo(component).startDate | formatDate('Do MMM, HH:mm')}}
                        </template>
                        <template v-else>Send date: -</template>
                      </span>
                    </template>
                    <template v-else-if="['collective-goal'].includes(componentInfo(component).key)">
                    </template>
                    <template v-else>
                      <br><span style="font-size: 13px" :class="{ 'text-muted': !(activeComponent && component.id === activeComponent.id) }">
                        <template v-if="componentInfo(component).startDate">
                          Start date: {{componentInfo(component).startDate | formatDate('Do MMM, HH:mm')}}
                        </template>
                        <template v-else>Start date: -</template>
                      </span>
                      <br><span style="font-size: 13px" :class="{ 'text-muted': !(activeComponent && component.id === activeComponent.id) }">
                        <template v-if="componentInfo(component).endDate">
                          End date: {{componentInfo(component).endDate | formatDate('Do MMM, HH:mm')}}
                        </template>
                        <template v-else>End date: -</template>
                      </span>
                    </template>
                    <br><b-link @click.prevent="deleteComponent(component)" :class="{
                      'text-danger': !(activeComponent && component.id === activeComponent.id),
                      'text-white': activeComponent && component.id === activeComponent.id
                    }" size="sm">Delete</b-link>
                  </div>
                </div>
              </b-card>
              <span
                class="plus-button"
                :style="{ background: form.branding ? form.branding.colour : '#5f27cd'}"
                @click="activeComponent = null"
              >
                <font-awesome-icon icon="plus"/>
              </span>
            </div>
            <div class="d-flex justify-content-center p-3 border-top">
              <span>End</span>
            </div>
          </div>
        </b-col>

        <b-col md="9" class="p-0 d-flex flex-column justify-content-between">
          <div>
            <div class="d-flex justify-content-start align-items-center px-3 py-2 border-bottom shadow-sm">
              <div>
                <b-img blank :blank-color="form.branding.colour" @click="showColourPicker = !showColourPicker"
                  class="cursor-pointer mr-3" height="36px" width="36px" rounded />
                <colour-picker class="mr-2" style="position: absolute; z-index: 100" v-show="showColourPicker"
                  :value="form.branding ? form.branding.colour : '#5f27cd'" @input="updateColour($event)" />
              </div>
              <b-input class="mr-3" style="max-width: 260px" v-model="form.title" placeholder="Name your campaign..." @change="updateCampaign"/>
              <b-input class="mr-3" v-model="form.tagline" placeholder="Tag line of your campaign…" @change="updateCampaign"/>
              <b-form-select v-model="form.status" :options="campaignStatuses"  @change="updateCampaign" style="max-width: 120px" />
            </div>
            <b-row class="m-0 pb-4 pr-4">

              <!-- COMPONENT -->
              <template v-if="activeComponent === null">
                <b-col v-for="{ key, label, icon, status } in allowedComponents" :key="key" md="6" class="pb-0 pr-0 pl-4 pt-4">
                  <b-card body-class="d-flex justify-content-between align-items-center" class="h-100">
                    <h4 class="m-0"><strong><span class="mr-3 text-muted"><font-awesome-icon :icon="icon" /></span>{{ label }}</strong></h4>
                    <b-button v-if="status === 'ACTIVE' || isDemoSite" variant="outline-primary px-3" size="sm" @click="createComponent(key)">+</b-button>
                    <span v-else class="text-secondary" style="font-size: 13px">Coming Soon</span>
                  </b-card>
                </b-col>
              </template>
              <template v-else-if="componentInfo(activeComponent).key === 'email'">
                <b-col md="12" class="p-4">
                  <update-email-message
                    :value="activeComponent"
                    :syncedEmailAddresses="syncedEmailAddresses"
                    @input="updateComponent($event)"
                  />
                </b-col>
              </template>
              <template v-else-if="componentInfo(activeComponent).key === 'slack'">
                <b-col md="12" class="p-4">
                  <update-slack-message :value="activeComponent" @input="updateComponent($event)" />
                </b-col>
              </template>
              <template v-else-if="componentInfo(activeComponent).key === 'referral-jam'">
                <b-col md="12" class="pl-3 pr-2 py-4">
                  <update-referral-jam :eventID="activeComponent.event.id" />
                </b-col>
              </template>
              <template v-else-if="componentInfo(activeComponent).key === 'employee-leaderboard'">
                <b-col md="12" class="p-4">
                  <update-leaderboard :value="activeComponent" @input="updateComponent($event)" />
                </b-col>
              </template>
              <template v-else-if="componentInfo(activeComponent).key === 'raffle-leaderboard'">
                <b-col md="12" class="p-4">
                  <update-leaderboard :value="activeComponent" @input="updateComponent($event)" />
                </b-col>
              </template>
              <template v-else-if="componentInfo(activeComponent).key === 'department-leaderboard'">
                <b-col md="12" class="p-4">
                  <update-leaderboard :value="activeComponent" @input="updateComponent($event)" />
                </b-col>
              </template>
              <template v-else-if="componentInfo(activeComponent).key === 'collective-goal'">
                <b-col md="12" class="p-4">
                  <update-collective-goal
                    :value="activeComponent"
                    @input="updateComponent($event)"
                  />
                </b-col>
              </template>
              <template v-else-if="componentInfo(activeComponent).key === 'reminder'">
                <b-col md="12" class="p-4">
                  <update-reminder :value="activeComponent" @input="updateComponent($event)"/>
                </b-col>
              </template>

            </b-row>
          </div>
          <div class="d-flex justify-content-end align-items-center px-3 py-2 border-top">
            <!-- b-button @click="console(form)">Console</b-button -->
            <b-button
              class="px-3"
              variant="outline-primary"
              @click="$router.push({ name: 'EditCampaignJobs', params: { campaignID: campaign.id } })"
            >Next</b-button>
          </div>
        </b-col>
      </b-row>
    </campaign-editor>
  </page>
</template>

<script>
import gql from 'graphql-tag';
import formatDate from 'date-fns/format';
import { Twitter as ColourPicker } from 'vue-color';
import CampaignAdminStagesQuery from '@/graphql/queries/CampaignAdminStages.gql';
import UpdateCampaignMutation from '@/graphql/mutations/UpdateCampaign.gql';
import CreateCampaignComponentMutation from '@/graphql/mutations/CreateCampaignComponent.gql';
import UpdateCampaignComponentMutation from '@/graphql/mutations/UpdateCampaignComponent.gql';
import Page from '@/components/campaign-admin/page';
import CampaignEditor from '@/components/campaign-admin/campaign-editor-frame';
import UpdateEmailMessage from '@/components/campaign-admin/update-email-message';
import UpdateSlackMessage from '@/components/campaign-admin/update-slack-message';
import UpdatePushMessage from '@/components/campaign-admin/update-push-message';
import UpdateLeaderboard from '@/components/campaign-admin/update-leaderboard';
import UpdateReferralJam from '@/components/campaign-admin/update-referral-jam';
import UpdateReminder from '@/components/campaign-admin/update-reminder';
import UpdateCollectiveGoal from '@/components/campaign-admin/update-collective-goal.vue';
import { mapRoot } from '@src/mixins/root';

const CreateEventMutation = gql`
  mutation CreateEvent($create: CreateEventInput!) {
    event: createEvent(create: $create) {
      id
    }
  }
`;

const DeleteCampaignComponentMutation = gql`
  mutation CreateEvent($id: ID!, $campaignID: ID!) {
    deleteCampaignComponent(id: $id, campaign: { id: $campaignID })
  }
`;

const SyncedEmailAddressesQuery = gql`
  query SyncedEmailAddresses {
    company {
      id
      users(
        where: {
          NOT: { AND: [ { state: null }, { state: { emailProvider: null } } ] }
        }
      ) {
          id
          emailAddress
          fullName
      }
    }
  }
`;

const allowedComponents = [ {
  key: 'email',
  label: 'Scheduled Email',
  icon: 'envelope',
  status: 'ACTIVE',
}, {
  key: 'slack',
  label: 'Scheduled Slack Message',
  icon: [ 'fab', 'slack' ],
  status: 'INACTIVE',
}, {
  key: 'push',
  label: 'Scheduled Push Notification',
  icon: [ 'fa', 'mobile' ],
  status: 'INACTIVE',
}, {
  key: 'referral-jam',
  label: 'Referral Jam',
  icon: 'calendar',
  status: 'ACTIVE',
}, {
  key: 'employee-leaderboard',
  label: 'Employee Leaderboard',
  icon: 'trophy',
  status: 'ACTIVE',
}, {
  key: 'department-leaderboard',
  label: 'Department Leaderboard',
  icon: 'medal',
  status: 'INACTIVE',
}, {
  key: 'raffle-leaderboard',
  label: 'Raffle Leaderboard',
  icon: 'tag',
  status: 'ACTIVE',
}, {
  key: 'collective-goal',
  label: 'Collective Goal',
  icon: 'bullseye',
  status: 'ACTIVE',
}, {
  key: 'reminder',
  label: 'Reminder',
  icon: 'bell',
  status: 'INACTIVE',
} ];

const scores = [
  {
    id: 'shares',
    icon: 'link',
    label: 'Shares',
    value: 1,
    eventNames: [ 'JOB_SHARED', 'CONTENT_SHARED', 'JOB_BROWSE_LINKEDIN', 'JOB_SCHEDULE_SHARE_FIRST', 'CONTENT_SCHEDULE_SHARE_FIRST', 'EVENT_REFERRAL_CREATED', 'REFERRAL_SUGGESTED' ],
    maximum: 5,
  },
  {
    id: 'applications',
    icon: 'file-alt',
    label: 'Applications',
    value: 100,
    eventNames: [ 'APPLICATION_CREATED' ],
  },
  {
    id: 'interviews',
    icon: 'users',
    label: 'Interviews',
    value: 300,
    eventNames: [ 'APPLICATION_INTERVIEW' ],
  },
  {
    id: 'hires',
    icon: 'handshake',
    label: 'Hires',
    value: 1000,
    eventNames: [ 'APPLICATION_HIRED' ],
  }
];

export default {
  name: 'CampaignAdminStages',
  components: {
    Page,
    CampaignEditor,
    ColourPicker,
    UpdateReferralJam,
    UpdateEmailMessage,
    UpdateSlackMessage,
    UpdatePushMessage,
    UpdateLeaderboard,
    UpdateReminder,
    UpdateCollectiveGoal
  },
  props: {
    campaignID: {
      type: String,
    },
  },
  data() {
    return {
      showColourPicker: false,
      activeComponent: null,
      form: {
        branding: {
          colour: '#5f27cd',
        },
        title: '',
        tagline: '',
        status: 'DRAFT',
        startDate: null,
        endDate: null,
        components: [],
      }
    };
  },
  apollo: {
    campaign: {
      query: CampaignAdminStagesQuery,
      variables() {
        const { campaignID } = this;
        return { campaignID };
      },
      update({ company }) {
        const { campaign } = company;
        return campaign;
      },
      result({ data }) {
        console.log(data);
        const { campaign } = data.company;
        console.log('CAMPAIGN BEFORE FORM IS SET: ', campaign);
        this.setForm(campaign);
      },
      error(err) {
        this.$notifyFatalErr(err, { prepend: 'Failed to load the campaigns' });
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
    syncedEmailAddresses: {
      query: SyncedEmailAddressesQuery,
      update({ company }) {
        const { users } = company || {};
        return users;
      },
      error(err) {
        this.$notifyFatalErr(err, { prepend: 'Failed to load the synced email addresses' });
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
  },
  methods: {
    changeComponent(component) {
      this.activeComponent = 'loading';
      setTimeout(() => {
        this.activeComponent = component;
      }, 100);
    },
    setForm(campaign) {
      const {
        branding,
        title,
        tagline,
        status,
        endDate,
        startDate,
        components,
      } = campaign;
      console.log('FORM COMPONENTS: ', JSON.parse(JSON.stringify(components)));
      const form = {
        branding: branding ? { ...branding } : { colour: '#5f27cd' },
        title,
        tagline,
        endDate,
        startDate,
        status,
        components: components ? [ ...components ] : [],
      };
      delete form.branding.__typename;
      this.form = form;
    },
    async updateCampaign() {
      const { form, campaignID: id } = this;
      const update = { ...form };
      update.title = update.title || null;
      update.tagline = update.tagline || null;
      update.startDate = update.startDate || null;
      update.endDate = update.endDate || null;
      delete update.components;
      console.log({ update });
      try {
        await this.$apollo.mutate({
          mutation: UpdateCampaignMutation,
          variables: {
            where: { id },
            update
          },
        });
      } catch (error) {
        // @TODO: Display an error on the UI
        console.log(error);
      }
    },
    updateColour(input) {
      if (!input) return;
      const { hex } = input;
      this.form.branding = this.form.branding || {};
      this.form.branding.colour = hex;
      this.updateCampaign();
    },
    async createComponent(key) {
      const component = allowedComponents.find(({ key: k }) => k === key);
      if (!component) return;
      const { campaignID: id } = this;

      const create = {};
      if (key === 'email') {
        create.emailMessage = {
          from: 'NONE',
          to: 'ALL_USERS',
          status: 'DRAFT',
        };
      } else if (key === 'slack') {
        create.slackMessage = {
          status: 'DRAFT',
        };
      } else if (key === 'push') {
        create.pushMessage = {
          status: 'DRAFT',
        };
      } else if (key === 'referral-jam') {
        const { data, errors } = await this.$apollo.mutate({
          mutation: CreateEventMutation,
          variables: {
            create: {
              title: 'Referral Jam',
            },
          },
        });
        if (Array.isArray(errors)) {
          errors.forEach(err => this.$log.error('createEvent', err));
          throw errors.shift();
        }
        this.$assert(data && data.event && data.event.id, 404, 'Failed to create new event');
        const { event } = data;
        const { id: eventID } = event || {};
        create.event = {
          id: eventID,
        };
      } else if (key === 'employee-leaderboard') {
        create.leaderboard = {
          type: 'USERS',
          raffleMode: false,
          scores,
        };
      } else if (key === 'raffle-leaderboard') {
        create.leaderboard = {
          type: 'USERS',
          raffleMode: true,
          scores,
        };
      } else if (key === 'department-leaderboard') {
        create.leaderboard = {
          type: 'DEPARTMENTS',
          raffleMode: false,
          scores,
        };
      } else if (key === 'reminder') {
        create.reminder = {
          status: 'DRAFT',
          to: this.user ? this.user.emailAddress : '',
        };
      } else if (key === 'collective-goal') {
        create.collectiveGoal = {
          status: 'DRAFT',
        };
      }
      console.log({ create });
      try {
        const { campaignID } = this;
        await this.$apollo.mutate({
          mutation: CreateCampaignComponentMutation,
          variables: {
            campaign: { id },
            create,
          },
          update(store, { data: { createCampaignComponent } }) {
            const { company } = store.readQuery({
              query: CampaignAdminStagesQuery,
              variables: { campaignID }
            });
            if (company.campaign && Array.isArray(company.campaign.components)) {
              company.campaign.components.push(createCampaignComponent);
            } else {
              company.campaign.components = [ createCampaignComponent ];
            }
            company.campaign.components.map(el => {
              if (el.emailMessage) {
                el.emailMessage.__typename = 'EmailMessage';
              }
              if (el.leaderboard) {
                el.leaderboard.__typename = 'Leaderboard';
              }
              return el;
            });
            console.log({
              query: CampaignAdminStagesQuery,
              variables: { campaignID },
              data: {
                company
              }
            });
            store.writeQuery({
              query: CampaignAdminStagesQuery,
              variables: { campaignID },
              data: {
                company
              }
            });
          },
        });
        this.$notification({ message: 'Component is successfully added', variant: 'success' });
      } catch (error) {
        this.$notifyErr(error, { prepend: 'Failed to save component' });
        console.log(error);
      }
    },
    async updateComponent(update) {
      const { campaignID: id, activeComponent } = this;
      try {
        if (update.leaderboard) {
          update.leaderboard = {
            // @TODO remove hard coded value once department leaderboard works
            type: 'USERS',
            ...update.leaderboard,
          };
        }
        await this.$apollo.mutate({
          mutation: UpdateCampaignComponentMutation,
          variables: {
            campaign: { id },
            componentID: activeComponent.id,
            update,
          },
          update: async () => {
            if (update.leaderboard) {
              const { startDate, endDate } = update.leaderboard;
              if (startDate || endDate) {
                setTimeout(async () => {
                  await this.updateCampaignDates();
                }, 500);
              }
            }
          }
        });
        this.$notification({ message: 'Component is successfully updated', variant: 'success' });
      } catch (error) {
        this.$notifyErr(error, { prepend: 'Failed to update component' });
        console.log(error);
      }
    },
    async updateCampaignDates() {
      const { campaign } = this;
      const { components } = campaign || {};
      if (Array.isArray(components) && components.length) {
        const { startDate, endDate } = components.reduce((o, { leaderboard, event }) => {
          const { startDate, endDate } = leaderboard || event || {};
          if ((startDate && !o.startDate) || (startDate < o.startDate)) {
            o.startDate = startDate;
          }
          if ((endDate && !o.endDate) || (endDate > o.endDate)) {
            o.endDate = endDate;
          }
          return o;
        }, { startDate: null, endDate: null });
        this.form.startDate = startDate;
        this.form.endDate = endDate;
        await this.updateCampaign();
      }
    },
    async deleteComponent(component) {
      setTimeout(() => { this.activeComponent = null; }, 300);
      try {
        const { id } = component;
        const { campaignID } = this;
        await this.$apollo.mutate({
          mutation: DeleteCampaignComponentMutation,
          variables: { id, campaignID },
          update: async store => {
            const { company } = store.readQuery({
              query: CampaignAdminStagesQuery,
              variables: { campaignID }
            });

            if (company.campaign && Array.isArray(company.campaign.components)) {
              const index = company.campaign.components.findIndex(({ id: componentID }) => id === componentID);
              console.log('INDEX: ', index);
              company.campaign.components.splice(index, 1);
              // company.campaign.components.map(el => {
              //   if (el.emailMessage) {
              //     el.emailMessage.__typename = 'EmailMessage';
              //   }
              //   if (el.leaderboard) {
              //     el.leaderboard.__typename = 'Leaderboard';
              //   }
              //   return el;
              // });
            }
            console.log('WRITE: ', {
              query: CampaignAdminStagesQuery,
              variables: { campaignID: id },
              data: {
                company
              }
            });
            store.writeQuery({
              query: CampaignAdminStagesQuery,
              variables: { campaignID: id },
              data: {
                company
              }
            });
            setTimeout(async () => {
              await this.updateCampaignDates();
            }, 500);
          },
        });
      } catch (error) {
        // @TODO: Display an error on the UI
        console.log(error);
      }
    },
    componentInfo(component) {
      const { event, leaderboard, emailMessage, slackMessage, pushMessage, collectiveGoal, reminder } = component;
      const { parseDate } = this;
      if (event) {
        const { key, label, icon } = allowedComponents.find(({ key: k }) => k === 'referral-jam');
        const { startDate, endDate } = event;
        return { key, label, icon, startDate: parseDate(startDate), endDate: parseDate(endDate) };
      } else if (leaderboard && leaderboard.type === 'USERS' && !leaderboard.raffleMode) {
        const { key, label, icon } = allowedComponents.find(({ key: k }) => k === 'employee-leaderboard');
        const { startDate, endDate } = leaderboard;
        return { key, label, icon, startDate: parseDate(startDate), endDate: parseDate(endDate) };
      } else if (leaderboard && leaderboard.type === 'USERS' && leaderboard.raffleMode) {
        const { key, label, icon } = allowedComponents.find(({ key: k }) => k === 'raffle-leaderboard');
        const { startDate, endDate } = leaderboard;
        return { key, label, icon, startDate: parseDate(startDate), endDate: parseDate(endDate) };
      } else if (leaderboard && leaderboard.type === 'DEPARTMENTS') {
        const { key, label, icon } = allowedComponents.find(({ key: k }) => k === 'department-leaderboard');
        const { startDate, endDate } = leaderboard;
        this.parseDate(startDate);
        return { key, label, icon, startDate: parseDate(startDate), endDate: parseDate(endDate) };
      } else if (emailMessage) {
        const { key, label, icon } = allowedComponents.find(({ key: k }) => k === 'email');
        const { sendAt } = emailMessage;
        return { key, label, icon, startDate: parseDate(sendAt) };
      } else if (slackMessage) {
        const { key, label, icon } = allowedComponents.find(({ key: k }) => k === 'slack');
        const { sendAt } = slackMessage;
        return { key, label, icon, startDate: parseDate(sendAt) };
      } else if (pushMessage) {
        const { key, label, icon } = allowedComponents.find(({ key: k }) => k === 'push');
        const { sendAt } = pushMessage;
        return { key, label, icon, startDate: parseDate(sendAt) };
      } else if (reminder) {
        console.log('reminder IS: ', reminder);
        const { key, label, icon } = allowedComponents.find(({ key: k }) => k === 'reminder');
        const { sendAt } = reminder;
        return { key, label, icon, startDate: parseDate(sendAt) };
      } else if (collectiveGoal) {
        const { key, label, icon } = allowedComponents.find(({ key: k }) => k === 'collective-goal');
        return { key, label, icon };
      }
      return {};
    },
    parseDate(dateString) {
      if (!dateString) return null;
      let date = Date.parse(dateString);
      if (isNaN(date)) {
        date = new Date(parseInt(dateString));
      }
      if (isNaN(date)) {
        date = new Date(dateString);
      }
      if (!isNaN(date)) {
        return date.getTime();
      }
      return null;
    },
    console(variable) {
      console.log({ variable });
    }
  },
  watch: {
    activeComponent() {
      console.log('ACTIVE COMPONENT: ', this.activeComponent);
      console.log('ACTIVE FORM: ', this.form);
    }
  },
  computed: {
    ...mapRoot([ 'user', 'isDemoSite' ]),
    allowedComponents: () => allowedComponents,
    formatDate: () => formatDate,
    campaignStatuses: () => ([
      { value: 'DRAFT', text: 'Draft' },
      { value: 'READY', text: 'Ready' },
      { value: 'IN_PROGRESS', text: 'Live' },
      { value: 'FINISHED', text: 'Finished' },
      { value: 'ARCHIVED', text: 'Archived' }
    ])
  },
};
</script>

<style lang="scss">
.stages-editor {
  .vertical-line {
    width: 0px;
    position: absolute;
    top: 54px;
    bottom: 54px;
    z-index: 0;
  }
  .plus-button {
    display: block;
    width: 36px;
    line-height: 36px;
    text-align: center;
    color: #fff;
    border-radius: 18px;
    z-index: 1;
    cursor: pointer;

    &:hover {
      opacity: 0.9;
    }

    &:active {
      opacity: 0.95;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/campaign-admin/Stages.vue